import React, { useEffect, useId, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { HelpText } from '../helpTexts';
import { launchColors } from '../launchTheme';

const InfoTip: React.FC<{ text: HelpText }> = ({ text }) => {
  const describedBy = useId();
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const [pinned, setPinned] = useState(false);
  const open = hover || focus || pinned;

  useEffect(() => {
    if (!pinned) return undefined;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node | null;
      const button = document.getElementById(`${describedBy}-button`);
      const tip = document.getElementById(describedBy);
      if (target && (button?.contains(target) || tip?.contains(target))) return;
      setPinned(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPinned(false);
        setHover(false);
        setFocus(false);
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [describedBy, pinned]);

  return (
    <>
      <Tooltip
        open={open}
        arrow
        placement="top"
        disableFocusListener
        disableHoverListener
        disableTouchListener
        title={<HelpBody text={text} />}
        slotProps={{
          arrow: { sx: { color: '#F7F5F0', '&::before': { border: `1px solid ${launchColors.sand}` } } },
          tooltip: {
            id: describedBy,
            sx: {
              bgcolor: '#F7F5F0',
              color: launchColors.sandDark,
              maxWidth: 320,
              p: 1.25,
              borderRadius: '8px',
              border: `1px solid ${launchColors.sand}`,
              boxShadow: '0 8px 24px rgba(100,88,57,0.16)',
            },
          },
        }}
      >
        <IconButton
          id={`${describedBy}-button`}
          size="small"
          aria-label={`Mer information: ${text.title}`}
          aria-describedby={open ? describedBy : undefined}
          aria-expanded={open}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          onClick={(event) => {
            event.stopPropagation();
            setPinned((value) => !value);
          }}
          sx={{
            width: 28,
            height: 28,
            color: open ? launchColors.navy : launchColors.textSecondary,
            '&:hover, &.Mui-focusVisible': { color: launchColors.navy, bgcolor: launchColors.softNavy },
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Box
        component="span"
        aria-live="polite"
        sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}
      >
        {pinned ? `${text.title}. ${text.body}` : ''}
      </Box>
    </>
  );
};

const HelpBody: React.FC<{ text: HelpText }> = ({ text }) => (
  <Box>
    <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.45, color: launchColors.sandDark }}>{text.title}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 400, lineHeight: 1.45, color: launchColors.sandDark, mt: 0.25 }}>{text.body}</Typography>
    {text.items && (
      <Box component="ul" sx={{ m: 0, mt: 0.75, pl: 2 }}>
        {text.items.map((item) => (
          <Typography component="li" key={item.label} sx={{ fontSize: 13, lineHeight: 1.45, color: launchColors.sandDark }}>
            <Box component="span" sx={{ fontWeight: 700 }}>{item.label}</Box>
            {` – ${item.text}`}
          </Typography>
        ))}
      </Box>
    )}
  </Box>
);

export default InfoTip;
